import { useEffect, useRef, useState, useCallback } from 'react';
import { TabBar, type TabDef } from './components/TabBar';
import { InviteCard } from './components/InviteCard';
import { MoreMenu, type MorePick } from './components/MoreMenu';
import { useSession } from './hooks/useSession';
import { useThread } from './hooks/useThread';
import { useKeyboardInset } from './hooks/useKeyboardInset';
/* UX item 5 (H5/H6): drafts, filters and scroll offsets survive a remount. */
import { useScrollMemory } from './hooks/useUiMemory.ts';
import { getToken, authApi, getLastIdentity, clearLastIdentity } from './lib/api';
/* Card 9QADJv3CdiOh — resolve a member's picked reply colour key to its hex
   (shared list, measured AA on the mustard partner bubble). */
import { replyColourHex } from '../../shared/types';
import { InboxAddress } from './components/InboxAddress';
import { Pair } from './screens/Pair';
import { Thread } from './screens/Thread';
import type { ComposeTile } from './components/ComposeMenu';
import { UsPanel } from './screens/Us';
import { LifePanel } from './screens/Life';
import { AIPanel } from './screens/AI';
import { NotebookPanel } from './screens/Notebook';
import { Settings, settingsTabFromHash } from './screens/Settings';
import { Help } from './screens/Help';
import { Vault } from './screens/Vault';
import { Landing, signIn as landingSignIn } from './screens/Landing';
import { Onboarding } from './components/Onboarding';
import { FirstAct, FIRST_ACT_KEY } from './components/FirstAct';
import { PairCeremony } from './components/PairCeremony';
import { SoloSealedStack } from './components/SoloSealedStack';
import { UpdateToast } from './ui/UpdateToast';
import { ReferralToast } from './ui/ReferralToast';
import { PromoToast } from './ui/PromoToast';
import { InterestsNudgeToast } from './ui/InterestsNudgeToast';
import { applyPendingReferral } from './lib/referrals';
import { clearPendingShare, readPendingShare, type PearShare } from './lib/shareTarget';
import { applyPendingPromo } from './lib/promo';
import { UpgradeSheet } from './components/UpgradeSheet';
import { LegalFooter } from './components/LegalFooter';
/* Error visibility (card bdsxKQG9HrOQ): each panel gets its own boundary so a
   crash in one screen keeps the header and the bottom nav alive. */
import { PanelBoundary } from './ui/ErrorBoundary';

/** Short weekday + day + month in the device's own locale — the header date. */
function headerDate(): string {
  return new Date().toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
}

/** Inline SVG icons for the four intent tabs. 20×20, currentColor stroke. */
const Icons = {
  thread: (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 3h12a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z" />
      <path d="M7 8h6M7 12h4" />
    </svg>
  ),
  us: (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="6.5" cy="6.5" r="2.5" />
      <circle cx="13.5" cy="6.5" r="2.5" />
      <path d="M3 16c0-2.5 2.5-4.5 5-4.5h4c2.5 0 5 2 5 4.5" />
    </svg>
  ),
  life: (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="2" width="14" height="16" rx="2" />
      <path d="M7 6h6M7 10h6M7 14h4" />
    </svg>
  ),
  notebook: (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 3h10a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z" />
      <path d="M7 3v14" />
    </svg>
  ),
};

type Tab = 'thread' | 'us' | 'life' | 'notebook';

/** Explicit destination (card Mn55eOvFgvC0, review H4): a deep link names its
 *  target section AND subsection (plus an optional entity) so it always lands
 *  where it promises — not on whichever subsection was visited last. Every
 *  card callback collapses to one of these, routed through navigateTo. */
export interface Dest {
  section: Tab;
  subsection?: string;
  entityId?: string;
}

/** Serialize a destination to its URL hash (#us-game, #life-plan, …) so the
 *  visible screen is modelled in the address and browser Back retraces it. */
export function destHash(dest: Dest): string {
  return `#${dest.section}${dest.subsection ? `-${dest.subsection}` : ''}`;
}

/** Parse a destination hash back into a Dest (null if it isn't one). The
 *  inverse of destHash — the hashchange listener uses it to re-apply the
 *  screen a Back/Forward navigation landed on. */
export function destFromHash(hash: string): Dest | null {
  const m = /^#(thread|us|life|notebook)(?:-([a-z]+))?$/.exec(hash);
  return m ? { section: m[1] as Tab, subsection: m[2] } : null;
}

/** A compose-menu pick carried to a destination panel so it can open its own
 *  composer / action rather than just switching tabs (design v1.1 §5.2: the
 *  + menu is feature discovery — one tap shows everything Pear can create,
 *  and tapping one should land on THAT action). Consumed on mount. */
export type ComposeIntent =
  | { kind: 'ask' }
  | { kind: 'question' }
  | { kind: 'note' }
  | { kind: 'idea' }
  | { kind: 'memory' }
  | { kind: 'date' }
  | { kind: 'link' }
  | { kind: 'kudos' }
  | null;

/** Where each compose tile lands (card RvESgw7lVASX). The + menu is feature
 *  discovery, so every tile picks a target tab AND carries the intent the
 *  destination panel opens its own composer from (design v1.1 §5.2) — a tap
 *  has to end on the actual action, not on a tab switch. A lookup table
 *  rather than the nested ternary it replaced: nine tiles do not read as a
 *  conditional chain. `message` is handled before the lookup — it stays in
 *  the Thread and clears the intent. */
interface ComposeRoute {
  intent: ComposeIntent;
  tab: Tab;
  /** Sub-section to select on arrival (card Mn55eOvFgvC0, H4) — a tile that
   *  promises a specific surface (Plan, Dates, …) must land on it, not on the
   *  tab's default. */
  subsection?: string;
}

const COMPOSE_ROUTE: Record<Exclude<ComposeTile, 'message'>, ComposeRoute> = {
  ask: { intent: { kind: 'ask' }, tab: 'life' },
  question: { intent: { kind: 'question' }, tab: 'us' },
  kudos: { intent: { kind: 'kudos' }, tab: 'thread' },
  note: { intent: { kind: 'note' }, tab: 'notebook' },
  idea: { intent: { kind: 'idea' }, tab: 'notebook' },
  memory: { intent: { kind: 'memory' }, tab: 'notebook' },
  date: { intent: { kind: 'date' }, tab: 'notebook' },
  link: { intent: { kind: 'link' }, tab: 'notebook' },
};

/** Narrow ComposeIntent down to the tiles the Notebook panel acts on. */
function notebookIntent(intent: ComposeIntent) {
  return intent &&
    (intent.kind === 'note' ||
      intent.kind === 'idea' ||
      intent.kind === 'memory' ||
      intent.kind === 'date' ||
      intent.kind === 'link')
    ? intent
    : null;
}

const TABS: TabDef[] = [
  { key: 'thread', label: 'Thread', icon: Icons.thread },
  { key: 'us', label: 'Us', icon: Icons.us },
  { key: 'life', label: 'Life', icon: Icons.life },
  { key: 'notebook', label: 'Notebook', icon: Icons.notebook },
];

/**
 * Pear AppShell — React owns all rendering now.
 *
 * Replaces the hand-wired DOM in `index.html` + `main.ts`: showPair/showChat
 * become `status === 'pair'`, showTab becomes `tab` state (so the
 * `display:flex` vs `[hidden]` specificity bug that needed the
 * `[hidden] { display: none !important }` patch cannot occur), and the tab bar
 * carries one measured sliding indicator.
 */
export function App() {
  const [tab, setTab] = useState<Tab>('thread');
  // Token starts NULL (card yZexwSigXhw1): the session hook has not yet
  // checked that this device's couple token belongs to its own account, and
  // a socket opened before that check logs into the OLD room — which answered
  // bad_token and signed the person out of Pear entirely. The effect below
  // picks the token up one render later, once the session says 'chat'.
  const [token, setToken] = useState<string | null>(null);
  // Settings is a screen over the chat, not a sixth tab: opened from the
  // header, closed by Done or by picking a tab (card 0xhNPdS42FTh).
  const [settingsOpen, setSettingsOpen] = useState(false);
  // Which Settings section shows (card DI31pLXCFc2C): pinned submenus, so the
  // selection lives here — a deep link (#settings-us) and a visit back both
  // keep it, exactly like lifeTab / usTab below.
  const [settingsTab, setSettingsTab] = useState('you');
  // More menu (card yOcdjnR6pXBz): the 5th slot in the bottom bar opens the
  // full menu — every screen and sub-view as a quick link, Settings inside.
  const [moreOpen, setMoreOpen] = useState(false);
  /* Collapsible desktop rail (card quWhYyrNiIbt, PRD L2) — collapses to ~72px
     icons-only and the pick is remembered per browser. Phones have no rail, so
     the state is inert there (the toggle is hidden and the CSS is gated to
     >=641px); no separate mobile branch needed. */
  const [railCollapsed, setRailCollapsed] = useState<boolean>(() => {
    try { return window.localStorage.getItem('pear.rail.collapsed') === '1'; } catch { return false; }
  });
  const toggleRail = () => setRailCollapsed(prev => {
    const next = !prev;
    try { window.localStorage.setItem('pear.rail.collapsed', next ? '1' : '0'); } catch { /* private mode */ }
    return next;
  });
  // Quick-link requests the panels apply on mount/change (More menu picks).
  const [threadFilter, setThreadFilter] = useState<'all' | 'chat' | null>(null);
  const [notebookCollection, setNotebookCollection] = useState<string | null>(null);
  // Life / Us sub-tabs (card sjFxIbWi3UgN). Held HERE rather than inside the
  // panels: both panels unmount on every bottom-bar switch, so local state
  // would drop you back on the first tab each visit — and the More menu's
  // quick links need to select the tab they are about to scroll to.
  // Jonathan 2026-10-24 (card fGcB7X75adxv): Life's first tab is Inbox, so
  // that is the one you land on — Requests is reachable in one tap and the
  // compose deep-link below still selects it deliberately.
  const [lifeTab, setLifeTab] = useState('admin');
  const [usTab, setUsTab] = useState('questions');
  /** Card GMat5z0Zxutf: every +1 opens the date-preferences segment after the
   *  More menu's "Date preferences" quick link lands on the Dates tab.
   *  Consumed, not latched (review 2026-10-19): the panel calls back once it
   *  has opened the overlay and we drop the signal to 0, so an ordinary
   *  remount of the Dates tab later in the session no longer reopens
   *  preferences on top of the user. */
  const [usPrefsSignal, setUsPrefsSignal] = useState(0);
  const clearPrefsSignal = useCallback(() => setUsPrefsSignal(0), []);
  // AI panel is a sibling to regular panels; opened from Settings > Agent.
  const [showingAI, setShowingAI] = useState(false);
  // Phase-0 screens over the tabs, opened from the More menu (card
  // YMMXiFOdFgdF): one state instead of a fifth tab, same shape as Settings.
  /* Help is the only screen that still takes the panel slot from the tabs:
     reveal history lives in Us, Questions and routing rules in Life, Inbox
     (cards IlGn5h3p3wDk, oQXsyU5lUDtV). */
  const [extraView, setExtraView] = useState<'help' | 'vault' | null>(null);
  // The concierge's walk-through (Jonathan email 2026-10-07): opens from
  // More → Take the tour and Help → Take the tour, never on its own any more
  // (card Tw2G4BUmOF5o).
  const [tourOpen, setTourOpen] = useState(false);
  // The three-choice first act (card Tw2G4BUmOF5o): what replaces the modal
  // that used to open over the app on first arrival.
  const [firstActOpen, setFirstActOpen] = useState(false);
  const keyboardInset = useKeyboardInset();
  /* UX item 5 (H5/H6): exactly ONE .pear-panel is mounted at a time and the
     PANEL is what scrolls — .pear-main always fits its own column, so a ref on
     it would remember nothing. The ref follows whichever panel is up and the
     key names that screen, so Life, Us, Notebook, Settings and the extra views
     each come back where you left them. The Thread's stream scrolls inside
     .pear-thread and carries its own record. */
  const panelRef = useRef<HTMLElement | null>(null);
  const panelKey = extraView ? `extra:${extraView}` : settingsOpen ? 'settings' : showingAI ? 'ai' : tab;
  useScrollMemory(panelRef, `panel:${panelKey}`);
  const mountPanel = useCallback((el: HTMLDivElement | null) => {
    panelRef.current = el;
  }, []);

  const sessionRef = useRef<ReturnType<typeof useSession> | null>(null);
  const session = useSession(() => sessionRef.current?.dropCouple());
  sessionRef.current = session;

  // Card Vn4Up6kLOCc3 — recognised but signed out: who this device belongs
  // to, in email + name form only. Re-read whenever the session lifecycle
  // moves (sign-out lands on 'pair'), and dropped by "Not you?" on the pair
  // screen. Carries no token and no couple link — it cannot open anything.
  // Declared after `session` because the effect depends on its status.
  const [lastIdentity, setLastIdentity] = useState(() => getLastIdentity());
  useEffect(() => {
    setLastIdentity(getLastIdentity());
  }, [session.status]);
  const forgetIdentity = useCallback(() => {
    clearLastIdentity();
    setLastIdentity(null);
  }, []);

  const threadRef = useRef<ReturnType<typeof useThread> | null>(null);
  const thread = useThread({
    token,
    members: session.members,
    onInvalidToken: () => sessionRef.current?.dropCouple(),
  });
  threadRef.current = thread;

  // Keep the socket token in step with the session lifecycle.
  useEffect(() => {
    setToken(session.status === 'chat' ? getToken() : null);
    if (session.status === 'pair') {
      setTab('thread');
      setSettingsOpen(false);
      setExtraView(null);
    }
  }, [session.status]);

  // Dropping back to the pair screen must clear the thread too.
  useEffect(() => {
    if (!token) threadRef.current?.reset();
  }, [token]);

  // Deep link (card DI31pLXCFc2C): #settings-<section> opens Settings on
  // that section — a support message can say "Settings → Concierge" and ship
  // the link. Reads the hash on arrival and on hashchange (back/forward).
  useEffect(() => {
    const apply = () => {
      const key = settingsTabFromHash(window.location.hash);
      if (!key) return;
      setSettingsTab(key);
      setShowingAI(false);
      setExtraView(null);
      setSettingsOpen(true);
    };
    apply();
    window.addEventListener('hashchange', apply);
    return () => window.removeEventListener('hashchange', apply);
  }, []);

  // Back/forward retraces screen changes (card Mn55eOvFgvC0, H4): a
  // destination hash (#us-game, #life-plan, …) pushed by navigateTo is
  // re-applied on popstate, so the browser Back button steps between the
  // screens you actually visited instead of leaving the app. Only acts on
  // hashes that parse as a Dest — #settings- and empty hashes are ignored.
  useEffect(() => {
    const applyDest = () => {
      const dest = destFromHash(window.location.hash);
      if (!dest) return;
      setSettingsOpen(false);
      setShowingAI(false);
      setExtraView(null);
      if (dest.subsection) {
        if (dest.section === 'us') setUsTab(dest.subsection);
        if (dest.section === 'life') setLifeTab(dest.subsection);
      }
      setTab(dest.section);
    };
    window.addEventListener('popstate', applyDest);
    return () => window.removeEventListener('popstate', applyDest);
  }, []);

  // Closing Settings clears its hash (every close path: Done, a tab pick,
  // opening the concierge, leaving) — otherwise a reload would reopen a
  // screen the person just left.
  useEffect(() => {
    if (settingsOpen) return;
    if (settingsTabFromHash(window.location.hash)) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
  }, [settingsOpen]);

  // First arrival in the chat: deal the three-choice first act unless it has
  // already been dealt. The tour no longer opens itself — reading nine steps
  // instead of doing one thing was the finding this card fixes. The old tour
  // flag counts as "seen" too: anyone who finished the tour (including every
  // seeded walk) has been onboarded and does not need the card.
  //
  // Card PsFeOvVtF57K (founder email 2026-10-21: "the onboarding prompt
  // (To Begin) should be suppressed if the user has already completed actions
  // in the app"): the flag is per device and forgets a reinstall, so it is not
  // the only gate. Real activity is — an account that has already written to
  // the Thread has done the thing the card exists to start, and being told
  // "three ways to start" afterwards is noise. This waits for `historyLoaded`
  // so the check runs against the messages the server actually holds instead
  // of racing the first frame, and it counts only MY messages (partner
  // messages and system lines are not this user's actions).
  const hasMyActivity = useCallback(() => {
    const mine = thread.myMemberId
      ? thread.messages.some(m => m.fromMemberId === thread.myMemberId)
      : false;
    return mine || thread.echoes.length > 0;
  }, [thread.messages, thread.echoes, thread.myMemberId]);

  useEffect(() => {
    if (session.status !== 'chat') return;
    let seen = false;
    try {
      seen = localStorage.getItem(FIRST_ACT_KEY) === '1' || localStorage.getItem('pear.onboarded') === '1';
    } catch {
      seen = true; // storage unavailable — never trap anyone in a loop
    }
    // historyLoaded is the gate on the activity check: before it lands the
    // thread is empty for everyone, and an existing account would be dealt
    // the card for the length of one round trip.
    if (!seen && thread.historyLoaded && !hasMyActivity()) setFirstActOpen(true);
  }, [session.status, thread.historyLoaded, hasMyActivity]);

  /** Dismiss the first act for good — the flag is what stops it returning. */
  const dismissFirstAct = useCallback(() => {
    try {
      localStorage.setItem(FIRST_ACT_KEY, '1');
    } catch {
      /* private mode — it will offer itself again, which is the intent */
    }
    setFirstActOpen(false);
  }, []);

  // Same rule while the card is already on screen: the first message I send
  // (or any that arrives from the server as mine) dismisses it, flag and all.
  // Declared after dismissFirstAct — the dependency array reads it.
  useEffect(() => {
    if (firstActOpen && thread.historyLoaded && hasMyActivity()) dismissFirstAct();
  }, [firstActOpen, thread.historyLoaded, hasMyActivity, dismissFirstAct]);

  const onChat = session.status === 'chat';

  /**
   * Leave (card Vf23eSKKxDhX) — one place, one confirm, and now one that
   * actually happens on the server. Delegates the whole sequence to
   * session.leaveCouple(): the endpoint first, then the local drop. Returns
   * null on success or the sentence to show, so Settings can keep the person
   * in the couple with the server's reason when it refuses (a live
   * subscription, most likely). Only a real success closes this screen.
   */
  const leaveCouple = useCallback(async (): Promise<string | null> => {
    const failed = await session.leaveCouple();
    if (failed) return failed;
    threadRef.current?.reset();
    setSettingsOpen(false);
    return null;
  }, [session]);

  /**
   * Sign out (Jonathan email 2026-10-07: "there's no sign out only leave").
   * Revokes the ACCOUNT session server-side first — while the token is still
   * in localStorage — then drops the local couple + auth state and lands on
   * the pair screen. Nothing on the couple changes: the next sign-in with
   * email + password mints a fresh couple token and returns to the room.
   */
  const signOut = async () => {
    try {
      await authApi('/api/auth/logout', { method: 'POST' });
    } catch {
      /* session already dead server-side — the local clear is what matters */
    }
    threadRef.current?.reset();
    session.leave();
    // Card Vn4Up6kLOCc3 — leave() just recorded who signed out; refresh the
    // identity the pair screen greets them with (the effect above only runs
    // when the status MOVES, and a sign-out that started on 'pair' doesn't).
    setLastIdentity(getLastIdentity());
    setSettingsOpen(false);
  };

  /** Close settings and open the AI panel in place. */
  const onOpenAI = useCallback(() => {
    setSettingsOpen(false);
    setShowingAI(true);
  }, []);

  /** Close settings and open the full Help inventory (card FiLhF6LRwAAP). */
  const onOpenHelp = useCallback(() => {
    setSettingsOpen(false);
    setShowingAI(false);
    setExtraView('help');
  }, []);

  /** The tour is on demand, and it lives with Help inside Settings now
   *  (card yOszUUb0Nnc2) instead of taking a More-menu row of its own. */
  const onOpenTour = useCallback(() => {
    setSettingsOpen(false);
    setShowingAI(false);
    setExtraView(null);
    setTourOpen(true);
  }, []);

  /** One-shot navigation to an explicit destination (card Mn55eOvFgvC0, H4).
   *  Closes every overlay, picks the section AND its subsection, carries an
   *  optional entity id, and pushes the destination onto the URL hash so the
   *  browser Back button retraces screen changes instead of leaving the app. */
  const navigateTo = useCallback((dest: Dest) => {
    setSettingsOpen(false);
    setShowingAI(false);
    setExtraView(null);
    if (dest.subsection) {
      if (dest.section === 'us') setUsTab(dest.subsection);
      if (dest.section === 'life') setLifeTab(dest.subsection);
    }
    setTab(dest.section);
    const hash = destHash(dest);
    if (window.location.hash !== hash) {
      window.history.pushState(null, '', hash);
    }
  }, []);

  /* PWA share target (card jZn7vpkB7EG1): main.tsx parked the payload off
     /share before React mounted, and it is read ONCE here — held, not
     consumed, because a share that arrives signed-out has to survive sign-up
     before there is a Notebook to fill. Landing on the pair screen must not
     drop it: nothing is written to the couple's data until Save is pressed. */
  const [pendingShare, setPendingShare] = useState<PearShare | null>(() => readPendingShare());

  /* Once this device is a real couple, take the parked share to Notebook.
     Only here — the pair screen renders no panels, so routing any earlier
     would be a tab switch into a screen that is not mounted. */
  useEffect(() => {
    if (!pendingShare || session.status !== 'chat') return;
    navigateTo({ section: 'notebook' });
  }, [pendingShare, session.status, navigateTo]);

  /* The panel calls this the moment it has prefilled the bar: one-shot, so a
     tab away and back cannot re-open a capture the person already dismissed. */
  const consumePendingShare = useCallback(() => {
    clearPendingShare();
    setPendingShare(null);
  }, []);

  /** A More-menu pick: close the menu, land on the target, then do whatever
   *  the quick link promised (thread filter, notebook collection, scroll). */
  const handleMorePick = useCallback((pick: MorePick) => {
    setMoreOpen(false);
    if (pick.tour) {
      setTourOpen(true);
      return;
    }
    if (pick.settings) {
      setShowingAI(false);
      setExtraView(null);
      setSettingsOpen(true);
      return;
    }
    // Phase-0 screens (card YMMXiFOdFgdF) are panels over the tabs, not
    // tabs — they take the slot Settings takes, then get out of the way.
    if (pick.view) {
      setSettingsOpen(false);
      setShowingAI(false);
      setExtraView(pick.view);
      return;
    }
    // Route through navigateTo so a More pick lands on its named subsection
    // and joins the Back history like every other deep link.
    if (pick.tab) {
      navigateTo({
        section: pick.tab,
        subsection: pick.lifeSub ?? pick.usSub ?? undefined,
      });
    }
    // Card GMat5z0Zxutf: "Date preferences" jumps straight into preferences
    // once the Dates tab has mounted — bump the signal the panel listens for.
    if (pick.usPrefs) setUsPrefsSignal((n) => n + 1);
    if (pick.threadFilter) setThreadFilter(pick.threadFilter);
    if (pick.collection) setNotebookCollection(pick.collection);
    if (pick.scrollTo) {
      // The panel mounts on the same tick as the tab switch — wait a frame
      // so the target exists before we scroll it into view.
      window.setTimeout(() => {
        document.getElementById(pick.scrollTo!)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 120);
    }
  }, [navigateTo]);

  // Jonathan 2026-10-16 (msg_6b9579f2): name the pair, in signup order, with
  // "&" for "and" — not "Both online". The members array arrives ordered by
  // joined_at ASC (couple-room.ts member read), which IS signup order, so the
  // join needs no sort of its own.
  const presenceText = session.members.length === 2
    ? session.members.map(m => m.displayName).filter(Boolean).join(' & ') || 'Both online'
    : 'Waiting for your partner';

  /* Card J9hHjSZDFTCV — the header mark glows while BOTH of us are watching.
     Two independent gates: the worker must report PEAR_PRESENCE (`=== true`,
     so an old worker or a failed config fetch stays dark) AND the partner's
     own socket must be in the room's latest snapshot. A tab this device hides
     clears locally as well, so the glow never claims togetherness for a
     window nobody is looking at. */
  const [partnerOnline, setPartnerOnline] = useState(false);
  useEffect(() => {
    const onPresence = (ev: Event) => {
      const online = (ev as CustomEvent<{ online?: unknown }>).detail?.online;
      const partner = session.members.find(m => m.memberId !== session.memberId);
      setPartnerOnline(Array.isArray(online) && !!partner && online.includes(partner.memberId));
    };
    const onVisibility = () => {
      if (document.hidden) setPartnerOnline(false);
    };
    window.addEventListener('pear:presence', onPresence as EventListener);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('pear:presence', onPresence as EventListener);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [session.members, session.memberId]);
  const together =
    session.authConfig?.presence === true && partnerOnline && !document.hidden;

  /* Header date (Jonathan 2026-10-17). Short weekday + day + month, the
     device's own locale — no timezone logic, no server call. Held in state and
     refreshed at the next LOCAL midnight plus on foreground: a PWA left open
     overnight would otherwise keep yesterday's date until some unrelated
     render happened (pre-deploy review, 2026-10-17). No per-second timer. */
  const [todayLabel, setTodayLabel] = useState(() => headerDate());
  useEffect(() => {
    let timer: number | undefined;
    const arm = () => {
      const now = new Date();
      const next = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 2);
      timer = window.setTimeout(() => {
        setTodayLabel(headerDate());
        arm();
      }, Math.max(1000, next.getTime() - now.getTime()));
    };
    arm();
    const onReturn = () => {
      if (!document.hidden) setTodayLabel(headerDate());
    };
    document.addEventListener('visibilitychange', onReturn);
    window.addEventListener('focus', onReturn);
    return () => {
      if (timer) window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', onReturn);
      window.removeEventListener('focus', onReturn);
    };
  }, []);

  /* Referral redemption (card 3MWZ8Zs7jA7e): a friend who arrived on ?ref=
     is holding the code in localStorage, and the reward can only be redeemed
     once this device has a couple token. The one-attempt guard is keyed by the
     COUPLE, not the launch (pre-deploy review, 2026-10-17): signing out and
     forming another couple on the same phone must still be able to redeem.
     Silent on every failure — a dead or already-used link must never interrupt
     sign-up or pairing. A successful apply dispatches the event ReferralToast
     shows. */
  const referralTriedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!getToken()) return;
    let coupleId = '';
    try {
      coupleId = localStorage.getItem('pear_couple_id') ?? '';
    } catch {
      return; // storage blocked: there is no per-couple key to guard with
    }
    if (!coupleId || referralTriedFor.current === coupleId) return;
    referralTriedFor.current = coupleId;
    void applyPendingReferral();
    // Card KpYLZuhhlcvC — a promo code captured on the account-creation
    // screen rides the same moment: once this device holds a couple, redeem
    // it (silent on refusal, same guard shape as the referral above).
    void applyPendingPromo();
  }, [session.status]);

  /** Compose-menu routing (integration wire): each tile picks a target tab
   *  AND carries a ComposeIntent so the destination panel opens its own
   *  composer (design v1.1 §5.2 — the + menu is feature discovery, so a tap
   *  lands on the actual action, not just a tab switch). */
  const [composeIntent, setComposeIntent] = useState<ComposeIntent>(null);

  /* One-shot intent (card 4vIN4UouHPz4): a deep-link intent must not outlive
     the screen that acts on it. Thread mounts only while its tab is active, so
     before this the intent survived every unmount and its mount effect
     re-fired on each return — the meeting dialog and the kudos sender
     resurrected themselves on unrelated screens after first use. */
  const clearComposeIntent = useCallback(() => setComposeIntent(null), []);

  const handleComposePick = useCallback((tile: ComposeTile) => {
    if (tile === 'message') {
      setComposeIntent(null);
      return; // stays in the thread; Thread refocuses its own input
    }
    const route = COMPOSE_ROUTE[tile];
    // Route through navigateTo so the compose tile lands on its named
    // subsection and joins the Back history (card Mn55eOvFgvC0).
    navigateTo({ section: route.tab, subsection: route.subsection });
    setComposeIntent(route.intent);
  }, [navigateTo]);

  /** A first-act pick (card Tw2G4BUmOF5o): land on the named destination with
   *  its own composer already open, so one tap ends on the action. */
  const goFirstAct = useCallback(
    (dest: Dest, intent: ComposeIntent = null) => {
      navigateTo(dest);
      setComposeIntent(intent);
    },
    [navigateTo],
  );

  return (
    <div
      id="app"
      className={`${onChat ? 'pear-app pear-has-nav' : 'pear-app'}${railCollapsed ? ' is-rail-collapsed' : ''}`}
      style={{ ['--kb-inset' as string]: `${keyboardInset}px` }}
      data-react-shell="app"
    >
      <header className={`pear-header${onChat ? '' : ' is-landing'}`}>
        <div className="pear-header-inner">
          <span className={`pear-mark${together ? ' is-together' : ''}`} />
          <h1 className="pear-title">Pear</h1>
          {/* The sign-up door sits ON the wordmark's line (founder email
              2026-10-25, card 5RluBbgTSKCs): it used to hold a row of its
              own above the beats, and the hairline under this header read as
              "a line at the top" of the landing page — both gone. Same id
              walk-landing waits for, same forwarder Landing exports. */}
          {!onChat && (
            <button
              type="button"
              className="landing-signin pear-header-signin"
              id="btn-landing-signin"
              onClick={landingSignIn}
            >
              Sign in / Sign up
            </button>
          )}
          {onChat && (
            <span className="pear-presence">{presenceText}</span>
          )}
          {/* Nav balance (Jonathan 2026-10-17): the current date sits in the
              header, in line with the partner names — the row had the couple
              on the left and nothing on the right. Date only, formatted from
              the device's own locale; no timezone logic, no server call. */}
          {onChat && <span className="pear-header-date">{todayLabel}</span>}
          {/* Settings lost its header slot — it lives in the More menu now
              (card yOcdjnR6pXBz: reachable only via More). */}
        </div>
      </header>

      <main className="pear-main">
        {/* Landing beats introduce the app to someone with nowhere else to be
            (card n3ZrSmsAlOpS). A partner arriving on an invite has somewhere
            specific to be — four screens of brand copy before the join box was
            the diversion the UX review named (item 3, H3), and an emailed
            invitee has already read enough to click. They get the join form on
            the first viewport instead. */}
        {!onChat && !session.pendingCode && !session.invitedLayout && <Landing />}
        {!onChat && (
          <Pair
            pendingCode={session.pendingCode}
            busy={session.busy}
            pairError={session.pairError}
            onRefreshInvite={session.refreshInvite}
            onClearPairError={session.clearPairError}
            authEmail={session.auth?.email ?? null}
            onCreate={name => void session.create(name)}
            onJoin={(code, name) => void session.join(code, name)}
            onJoinViaLink={(link, name) => void session.joinViaLink(link, name)}
            invitedLink={session.invitedLink}
            onSetInvitedLink={session.setInvitedLink}
            linkInfo={session.linkInfo}
            onRefreshLinkInfo={link => void session.refreshLinkInfo(link)}
            onSignIn={(email, password) => session.signIn(email, password)}
            onRegister={(email, password, name) => session.register(email, password, name)}
            oauthEnabled={session.authConfig?.oauth ?? false}
            oauthNotice={session.oauthNotice}
            verifyEnabled={session.authConfig?.verifyEmail ?? false}
            signupsOpen={session.authConfig?.signups === true}
            forgotEnabled={session.authConfig?.forgotPassword ?? false}
            mailNotice={session.mailNotice}
            invitedCode={session.invitedCode}
            invitedLayout={session.invitedLayout}
            onSetInvitedCode={session.setInvitedCode}
            resetToken={session.resetToken}
            emailVerified={session.emailVerified}
            onForgotPassword={session.forgotPassword}
            onApplyReset={session.applyResetPassword}
            onResendVerify={email => session.resendVerify(email)}
            onClearMailNotice={session.clearMailNotice}
            lastIdentity={lastIdentity}
            onForgetIdentity={forgetIdentity}
          />
        )}

        {/* Privacy / Terms footer on the signed-out page (card cEchedAassmt)
            — sits under the pair form so the landing scroll ends on it. */}
        {!onChat && <LegalFooter variant="page" />}

        {onChat && (
          <section
            className={`pear-chat fade-in`}
            id="chat-screen"
            /* The Thread has no panel wrapper of its own — it renders straight
               into this section — so the section IS its panel, but only while
               the Thread tab is the one up. Settings and the extra views take
               the whole surface over, and a tabpanel can only describe what is
               actually showing (UX item 6, H8). */
            role={
              tab === 'thread' && !extraView && !settingsOpen && !showingAI
                ? 'tabpanel'
                : undefined
            }
            aria-labelledby={
              tab === 'thread' && !extraView && !settingsOpen && !showingAI
                ? 'tab-thread'
                : undefined
            }
          >
            {/* TabBar renders as a bottom bar — see App.tsx bottom nav. */}
            {/* The code used to disappear with the pair screen, leaving a
                creator with nothing to send their partner. */}
            {/* The invite, as one card (cards AnqHIFLf6a5y + the Pair hero):
                the creator lands here, not on the Pair screen, so the share
                bar is where the invite actually has to leave the app —
                Copy / Share / Email / QR all read the same invite URL. */}
            {/* The invite blocks belong to the Thread, where the creator
                lands (Jonathan 2026-10-23: "on mobile there are excess
                lines/breaks at the top of the page" — on a solo couple they
                rode above EVERY tab: 468px of pair code + inbox address
                before Life/Notebook/Settings could start). Same condition
                the section uses to call itself the thread's tabpanel. */}
            {tab === 'thread' && !extraView && !settingsOpen && session.pendingCode && session.members.length < 2 && (
              <InviteCard
                code={session.pendingCode}
                mailEnabled={
                  (session.authConfig?.verifyEmail ?? false) ||
                  (session.authConfig?.forgotPassword ?? false)
                }
                idPrefix="chat-share-qr"
                variant="chat"
              />
            )}

            {/* The couple's shared inbox address at the invite moment (card
                qntf9Qa0Cemr, folded from 8ILRL6mrpBta) — the code's owner
                learns where to email before the partner even joins. Same
                server-issued code the Admin menu and Settings show. */}
            {tab === 'thread' && !extraView && !settingsOpen && session.pendingCode && session.members.length < 2 && (
              <InboxAddress
                code={session.pendingCode}
                idPrefix="invite"
                variant="inline"
                note="Once you're paired, email bills, bookings and receipts here — the concierge sorts each one into your shared space."
              />
            )}

            {/* Solo accumulation (card Pn1wrppZMWEr): the stack of sealed
                daily-question answers with its count — the solo home's proof
                that waiting is accumulating, not absence. It unmounts itself
                once paired, except for the unlock moment it holds open, and
                it rides the Thread tab only — a stack of cards above Life or
                Settings reads as spare line breaks at the top of the page
                (Jonathan 2026-10-23). */}
            {tab === 'thread' && !extraView && !settingsOpen && (
              <SoloSealedStack members={session.members} />
            )}

            {/* Panels mount only while their tab is active — the old shell
                kept all three in the DOM and lazily called mount() once.
                Settings replaces them while open; the tab bar stays so a tap
                on a tab is still a way out. */}
            {!extraView && settingsOpen && (
              <div id="panel-settings" className="pear-panel" ref={mountPanel}>
                <PanelBoundary>
                  <Settings
                    onClose={() => setSettingsOpen(false)}
                    onLeave={leaveCouple}
                    onSignOut={signOut}
                    onOpenAI={onOpenAI}
                    onOpenHelp={onOpenHelp}
                    onOpenTour={onOpenTour}
                    tab={settingsTab}
                    onTabChange={setSettingsTab}
                    pendingCode={session.pendingCode}
                    awaitingPartner={session.members.length < 2}
                    mailEnabled={
                      (session.authConfig?.verifyEmail ?? false) ||
                      (session.authConfig?.forgotPassword ?? false)
                    }
                  />
                </PanelBoundary>
              </div>
            )}

            {/* Full Help (card FiLhF6LRwAAP): the app's whole feature
                inventory in three beats, opened like the other extra views. */}
            {extraView === 'help' && (
              <div id="panel-help" className="pear-panel" ref={mountPanel}>
                <PanelBoundary>
                  <Help
                    onClose={() => setExtraView(null)}
                    onTour={() => {
                      setExtraView(null);
                      setTourOpen(true);
                    }}
                  />
                </PanelBoundary>
              </div>
            )}

            {/* Vault (card t193KwD1i7aY): the passphrase-gated room. The key lives
                inside the screen's own state, so closing this panel — or leaving
                the panel by any path — re-locks it; the wrapper is the same
                .pear-panel seam Help and Settings use. */}
            {extraView === 'vault' && (
              <div id="panel-vault" className="pear-panel" ref={mountPanel}>
                <PanelBoundary>
                  <Vault onClose={() => setExtraView(null)} />
                </PanelBoundary>
              </div>
            )}

            {/* AI panel — reachable from Settings > Concierge now that the AI tab is gone.
                It REPLACES the active tab panel rather than sitting beside it: with
                `tab` unchanged (Settings > Concierge never changes it) the old conditions
                rendered #panel-ai and #thread-scroll as two siblings in the flex
                column, so the AI screen stacked on top of the thread and — because
                Thread's chip effect runs once on mount — the invoke chip never came
                back after a permission was turned on. Gating every panel on
                !showingAI keeps exactly one mounted, and Thread remounts (and
                re-reads /api/ai/toggles) when the tab bar takes you back. */}
            {!extraView && !settingsOpen && showingAI && (
              <div id="panel-ai" className="pear-panel" ref={mountPanel}>
                <PanelBoundary>
                  <AIPanel />
                </PanelBoundary>
              </div>
            )}
            {!extraView && !settingsOpen && !showingAI && tab === 'notebook' && (
              <div id="panel-notebook" className="pear-panel" ref={mountPanel} role="tabpanel" aria-labelledby="tab-notebook">
                <PanelBoundary>
                  <NotebookPanel
                    requestedCollection={notebookCollection}
                    intent={notebookIntent(composeIntent)}
                    pendingShare={pendingShare}
                    onShareConsumed={consumePendingShare}
                  />
                </PanelBoundary>
              </div>
            )}
            {!extraView && !settingsOpen && !showingAI && tab === 'us' && (
              <div id="panel-us" className={`pear-panel${usTab === 'questions' ? ' panel-questions-wide' : ''}${usTab === 'dates' ? ' panel-dates-wide' : ''}`} ref={mountPanel} role="tabpanel" aria-labelledby="tab-us">
                <PanelBoundary>
                  <UsPanel
                    members={session.members}
                    intent={composeIntent?.kind === 'question' ? { kind: 'question' } : null}
                    activeTab={usTab}
                    onTabChange={setUsTab}
                    prefsSignal={usPrefsSignal}
                    onPrefsOpened={clearPrefsSignal}
                  />
                </PanelBoundary>
              </div>
            )}
            {!extraView && !settingsOpen && !showingAI && tab === 'life' && (
              <div id="panel-life" className="pear-panel" ref={mountPanel} role="tabpanel" aria-labelledby="tab-life">
                <PanelBoundary>
                  <LifePanel
                    intent={composeIntent?.kind === 'ask' ? { kind: 'ask' } : null}
                    activeTab={lifeTab}
                    onTabChange={setLifeTab}
                    members={session.members}
                  />
                </PanelBoundary>
              </div>
            )}

            {!extraView && !settingsOpen && !showingAI && tab === 'thread' && (
              <>
                {/* The Thread has no panel wrapper of its own, so it gets the
                    panel boundary directly — a composer crash must not take
                    the header and the tab bar with it (card bdsxKQG9HrOQ). */}
                <PanelBoundary>
            {/* The first act (card Tw2G4BUmOF5o) — three choices at the top
                of the Thread instead of a nine-step modal over the app. Inline
                and dismissable, so the tabs, the composer and the invite card
                behind it stay live. */}
            {firstActOpen && (
              <FirstAct
                paired={session.members.length >= 2}
                onDismiss={dismissFirstAct}
                onGo={goFirstAct}
              />
            )}
            <Thread
                /* Paired vs solo (empty state): two members means the invite
                   already landed, so the empty thread greets the couple
                   instead of pointing back at the pairing screen. */
                paired={session.members.length >= 2}
                requestedFilter={threadFilter}
                messages={thread.messages}
                echoes={thread.echoes}
                roleFor={thread.roleFor}
                nameFor={thread.nameFor}
                /* The partner's picked reply ink (card 9QADJv3CdiOh), resolved
                   off the same live members list as the name — a Settings
                   change repaints the feed on the next members broadcast. */
                colourFor={(memberId) =>
                  replyColourHex(session.members.find((m) => m.memberId === memberId)?.replyColour ?? null)}
                myMemberId={thread.myMemberId}
                onSend={thread.send}
                onRemoveMessage={thread.removeMessage}
                /* Card tPPB3ozS4YrK — the keystroke signal and its switch,
                   both read from the same places as the header glow: the hook
                   owns the socket (and the 2s window), the config owns whether
                   the feature exists at all. */
                onTyping={thread.typing}
                typingOn={session.authConfig?.typing === true}
                /* Windowed hydration (card 4ynbWszGdqUd): hasOlder drives the
                   "Show earlier messages" affordance, loadOlder pages back. */
                hasOlder={thread.hasOlder}
                onLoadOlder={thread.loadOlder}
                /* Archive (card 2sjo3uNvaRDr): find anything older than the
                   window, then page history to the row that holds it. */
                onSearch={thread.search}
                onJumpToSeq={thread.loadToSeq}
                loading={Boolean(token) && !thread.historyLoaded}
                onComposePick={handleComposePick}
                composeIntent={
                  composeIntent?.kind === 'kudos'
                    ? { kind: 'kudos' }
                    : null
                }
                /* One-shot intent (card 4vIN4UouHPz4): the panel clears it the
                   instant it acts, so returning to Thread can't resurrect the
                   kudos sender from a stale intent. */
                onIntentConsumed={clearComposeIntent}
                /* Explicit destinations (card Mn55eOvFgvC0, H4): one
                   navigateTo prop; each card names its own target subsection
                   (Game round → Us>Game+roundId, question history →
                   Us>Questions, locked date → Us>Dates, rail → Life>Chores). */
                onNavigate={navigateTo}
                onOpenGameRound={(roundId: string) => {
                  navigateTo({ section: 'us', subsection: 'game', entityId: roundId });
                }}
              />
                </PanelBoundary>
              </>
            )}
          </section>
        )}
      </main>

      {/* Nav — mobile: a bar fixed to the viewport bottom (thumb zone);
          desktop ≥641px: the same element becomes a pinned side menu down
          the left edge (card ESnACmbVhCYd). Both anchor to the layout
          viewport, so scrolling inside .pear-main or a panel never moves it. */}
      {onChat && (
        <TabBar
          tabs={TABS}
          active={tab}
          panelFor={key =>
            /* Panels mount one at a time (App.tsx), so the tab only controls
               a panel that exists right now — aria-controls never dangles. */
            settingsOpen || showingAI || extraView
              ? undefined
              : key === tab
                ? key === 'thread'
                  ? 'chat-screen'
                  : `panel-${key}`
                : undefined
          }
          onChange={k => {
            // Picking a tab is a way out of Settings — and out of the AI
            // screen, which has no tab of its own any more. Without this the
            // AI panel stayed mounted over whatever tab you picked.
            setSettingsOpen(false);
            setShowingAI(false);
            setExtraView(null);
            setTab(k as Tab);
          }}
          id="pear-tabs"
          trailing={
            <>
            <button
              type="button"
              className="pear-btn pear-tab more-slot"
              id="btn-more"
              aria-label="More"
              aria-haspopup="dialog"
              aria-expanded={moreOpen}
              onClick={() => setMoreOpen(true)}
            >
              <span className="tab-icon more-amp" aria-hidden="true">
                &amp;
              </span>
              {/* The word, not just the glyph: the slot reads "&" alone and
                  nobody can tell what it opens (founder report 2026-10-08 —
                  "I don't see the text More next to &"). Same label span the
                  four tabs carry, so the bar has one display. */}
              <span className="pear-tab-label">More</span>
            </button>
            {/* Collapse toggle (card quWhYyrNiIbt, PRD L2) — desktop only (the
                base .rail-collapse is display:none and only the >=641px block
                turns it on). A chevron that flips with the state, hidden label
                when collapsed like every other tab. */}
            <button
              type="button"
              className="pear-btn pear-tab rail-collapse"
              data-test="rail-collapse"
              aria-label={railCollapsed ? 'Expand menu' : 'Collapse menu'}
              aria-expanded={!railCollapsed}
              onClick={toggleRail}
            >
              <span className="tab-icon" aria-hidden="true">{railCollapsed ? '»' : '«'}</span>
              <span className="pear-tab-label">{railCollapsed ? 'Expand' : 'Collapse'}</span>
            </button>
            </>
          }
        />
      )}

      {/* The full menu — every screen and sub-view as a quick link, Settings
          inside (card yOcdjnR6pXBz). */}
      <MoreMenu open={moreOpen} onClose={() => setMoreOpen(false)} onPick={handleMorePick} onSignOut={() => void signOut()} />

      {/* App-update notice (card 7bLlo0He2hW0) — a bottom toast when this
          navigation handed over a newer build, instead of a hard reload. */}
      <UpdateToast />
      <ReferralToast />
      <PromoToast />
      {/* Interests nudge (card mMl44G43YK5X) — first-login-only toast; it
          reads its own eligibility (first-act undone, profile incomplete) and
          waits for the ceremony/first-act/tour overlays to clear, so mounting
          it beside the other toasts costs nothing on every later login. */}
      {onChat && <InterestsNudgeToast />}

      {/* Pear Plus paywall (card 32BEnNO6I2kx) — listens for the global
          pear:plus-required event that lib/api.ts fires on a 402, so every
          gated tap anywhere opens the same sheet. */}
      <UpgradeSheet />

      {/* The ceremonial pairing moment (card 2NuQ8dd2JMqs) — portalled to body,
          and mounted BEFORE the tour: both take the one shared overlay slot
          (UX item 6), so mount order decides who goes first. A newly paired
          couple sees the celebration, and the tour is granted the slot when it
          fades. Both devices get it — the partner from the room's `paired`
          frame, the joiner from its join response. */}
      {session.celebrate && <PairCeremony onDone={session.clearCelebrate} />}

      {/* The concierge's walk-through — portalled to body (see Onboarding.tsx). */}
      {tourOpen && <Onboarding onClose={() => setTourOpen(false)} />}
    </div>
  );
}
